/**
 * Minimum chart grid height (in px) required to display annotations.
 * Below this threshold, annotations are hidden to prevent visual clipping.
 * @internal
 */
export const MIN_HEIGHT_FOR_ANNOTATIONS = 160;

/**
 * Minimum chart grid height (in px) required to display top/bottom axes.
 * Below this threshold, axes are hidden to maximize the plot area.
 * @internal
 */
export const MIN_HEIGHT_FOR_AXES = 50;

/**
 * @internal
 */
export const enum AnnotationDisplayMode {
  Hidden = 'hidden',
  Compact = 'compact',
  Normal = 'normal',
}

/**
 * Determines whether annotations should be hidden, compact, or normal based on
 * chart height and whether compact annotations are enabled for the chart.
 * @internal
 */
export function getAnnotationDisplayMode(
  chartGridHeight: number,
  tracksQuantity: number,
  compactEnabled: boolean = false,
): AnnotationDisplayMode {
  if (tracksQuantity <= 0 || chartGridHeight < MIN_HEIGHT_FOR_AXES) {
    return AnnotationDisplayMode.Hidden;
  }

  if (chartGridHeight >= MIN_HEIGHT_FOR_ANNOTATIONS) {
    return AnnotationDisplayMode.Normal;
  }

  return compactEnabled
    ? AnnotationDisplayMode.Compact
    : AnnotationDisplayMode.Hidden;
}

/**
 * Determines whether annotations should be visible based on chart height
 * and whether annotation tracks exist.
 * @internal
 */
export function shouldShowAnnotations(
  chartGridHeight: number,
  tracksQuantity: number,
): boolean {
  return (
    getAnnotationDisplayMode(chartGridHeight, tracksQuantity) !==
    AnnotationDisplayMode.Hidden
  );
}

/**
 * Determines whether top/bottom axes should be visible based on chart height.
 * @internal
 */
export function shouldShowAxes(chartGridHeight: number): boolean {
  return chartGridHeight >= MIN_HEIGHT_FOR_AXES;
}
